import React from "react";
import {
  X,
  Loader,
  CheckCircle,
  XCircle,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

export default function AdjustmentDetailModal({
  isOpen,
  onClose,
  data,
  loading,
  onUpdateStatus,
}) {
  if (!isOpen) return null;

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "bg-green-50 border border-green-200 text-green-700";
      case "REJECTED":
        return "bg-red-50 border border-red-200 text-red-700";
      case "DRAFT":
        return "bg-yellow-50 border border-yellow-200 text-yellow-700";
      default:
        return "bg-gray-100 border border-gray-300 text-gray-700";
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "Disetujui";
      case "REJECTED":
        return "Ditolak";
      case "DRAFT":
        return "Draft";
      default:
        return status || "-";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Intl.DateTimeFormat("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(dateString));
    } catch {
      return dateString;
    }
  };

  const isDraft = data?.status?.toUpperCase() === "DRAFT";

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-2xl flex flex-col max-h-[90vh] overflow-y-auto modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-teal-600 text-white rounded-t-lg sticky top-0 z-10">
          <h2 className="text-lg font-bold">Detail Penyesuaian Stok</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        {!data ? (
          <div className="p-8 flex items-center justify-center text-gray-500">
            <Loader className="w-5 h-5 animate-spin mr-2" />
            Memuat data...
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {/* Meta info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">Nomor Penyesuaian</p>
                <p className="text-sm font-mono font-semibold text-gray-900">
                  {data.adjustment_number || "-"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Status</p>
                <span
                  className={`inline-block px-3 py-1 rounded border text-xs font-medium ${getStatusColor(data.status)}`}>
                  {getStatusLabel(data.status)}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Tanggal</p>
                <p className="text-sm text-gray-900">{formatDate(data.date)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">Dibuat Oleh</p>
                <p className="text-sm text-gray-900">
                  {data.created_by_name || "-"}
                </p>
              </div>
            </div>

            {data.notes && (
              <div className="border-t pt-4">
                <p className="text-xs text-gray-500 mb-1">Catatan</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                  {data.notes}
                </p>
              </div>
            )}

            {data.attachment_url && (
              <div className="border-t pt-4">
                <p className="text-xs text-gray-500 mb-1">Lampiran</p>
                <div className="mt-1">
                  {data.attachment_url.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                    <div className="relative rounded-lg overflow-hidden border border-gray-200 max-w-xs bg-gray-50">
                      <img
                        src={data.attachment_url}
                        alt="Lampiran Penyesuaian"
                        className="max-h-48 object-contain mx-auto"
                      />
                      <div className="p-2 bg-white text-center border-t">
                        <a
                          href={data.attachment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-semibold text-teal-600 hover:text-teal-700">
                          Buka Gambar Baru
                        </a>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={data.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-teal-600 hover:text-teal-700">
                      Buka Dokumen Lampiran (PDF/File)
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Items table */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Daftar Item ({data.items?.length || 0} produk)
              </p>
              {!data.items || data.items.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  Tidak ada item
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                          Produk
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                          Tipe
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                          Jumlah
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                          Alasan
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">
                              {item.product_name}
                            </p>
                            {item.product_code && (
                              <p className="text-xs text-gray-400">
                                {item.product_code}
                              </p>
                            )}
                            {item.variant_name && (
                              <p className="text-xs text-gray-500">
                                {item.variant_name}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {item.type === "IN" ? (
                              <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                                <ArrowUp className="w-3 h-3" /> Masuk
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                                <ArrowDown className="w-3 h-3" /> Keluar
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900">
                            {item.qty} {item.unit_name || "PCS"}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {item.reason || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {data && isDraft && (
          <div className="p-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
            <button
              onClick={() => onUpdateStatus("REJECTED")}
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2 border border-red-300 text-red-600 rounded-lg font-medium hover:bg-red-50 transition disabled:opacity-50">
              {loading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              Tolak
            </button>
            <button
              onClick={() => onUpdateStatus("APPROVED")}
              disabled={loading}
              className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition disabled:opacity-50">
              {loading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Setujui
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
